import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import svg from "../../utils/svg"
import styles from "./OnlyYouPaywall.module.scss"

/**
 * OnlyYouPaywall (`/onlyyou`) — an original, self-contained FEATURE-SPECIFIC
 * paywall for Private Photo Vault Premium. Paywall type rolled at random for
 * this run: FEATURE-SPECIFIC (a screen that heroes one real capability).
 *
 * ── Style directive (read live from #paywalls each run) ─────────────────────
 * The newest @Paywalls-Bot message this run was `focus on security and privacy`
 * (NOT the reset keyword `default`), so it is an ACTIVE STANDING DIRECTIVE at
 * super-high priority. The whole concept is built to it: the pitch is SECURITY
 * & PRIVACY, framed through the app's front door — the passcode that opens the
 * vault.
 *
 * ── Concept ────────────────────────────────────────────────────────────────
 * "The only key is the one only you know." The strongest lock isn't stored on
 * your phone or scanned off your face — it's a number that lives nowhere but
 * your memory. Nothing saved on a server, no face to copy, nothing on the chip
 * for anyone to lift: just a code only you hold. The hero is the vault's own
 * PASSCODE SCREEN — your library sits sealed as a faint mosaic behind a numeric
 * keypad; you (or the auto-demo) tap in a code, each pressed digit briefly shows
 * then EVAPORATES ("not saved anywhere"), the dots fill, and the vault seals to
 * "Opens only for you." The payoff is reassurance, not a photo reveal.
 *
 * ── Divergence (vs. the 80+ prior concepts, incl. the security cluster) ─────
 * The numeric PASSCODE KEYPAD is the one iconic vault mechanic no prior paywall
 * has built. Deliberately different from its neighbours:
 * Layout      : a full-screen VAULT LOCK SCREEN — keypad hero over a sealed
 *               photo mosaic. Not a centred enrolment console (Imprint), not a
 *               camera-roll→vault stage (Offscreen/Sightline/Cascade), not a
 *               home-screen springboard (PlainSight).
 * Interaction : TAP-A-NUMERIC-KEYPAD — discrete digit entry (0–9 + delete). New
 *               to the set: not press-to-enrol a fingerprint (Imprint), not
 *               draw-a-pattern through dots (Glyph), not rotate-a-dial (Tumbler).
 * Metaphor    : the KNOWLEDGE FACTOR — a secret that exists only in your head;
 *               pressed digits evaporate to show nothing is stored.
 * Value frame : SOLE KNOWLEDGE / nothing-to-steal — distinct from encryption
 *               (Cipher), hide-from-roll (Offscreen), disguise (PlainSight),
 *               break-in capture (Caught), biometric identity (Imprint).
 * CTA frame   : "make it only yours" — ownership of the one key.
 * Register    : calm, warm, high-assurance and a touch intimate. LIGHT warm
 *               ivory + soft brushed keys + brand blue — a deliberate turn away
 *               from the dark security console cluster (Cipher navy, Imprint
 *               obsidian-teal).
 *
 * ── Honest & App Store compliant ───────────────────────────────────────────
 * The keypad is the app's real passcode lock. The only-you lock (Face ID /
 * Touch ID / PIN), the encrypted Cloud Vault backup and the Decoy Vault are all
 * REAL Private Photo Vault features; the copy names them and claims nothing
 * else. The mosaic is labelled a demo library, not the user's photos. No fake
 * scarcity, no countdown, no data-loss-on-expiration threat (sealing protects
 * access; it never implies you lose photos). "Save 79%" is real arithmetic from
 * live product data. Yearly is the featured, default, most prominent price;
 * monthly is small & secondary. The 7-day free trial is the real trial.
 */

const CODE_LEN = 6

// bundled demo tiles (no network egress) — the "sealed library" behind the pad
const MOSAIC = [
  "/recall/sunset-1.jpg",
  "/recall/beach-2.jpg",
  "/recall/dog-1.jpg",
  "/recall/mountain-2.jpg",
  "/recall/city-1.jpg",
  "/recall/coffee-2.jpg",
  "/recall/flower-1.jpg",
  "/recall/food-2.jpg",
  "/recall/beach-3.jpg",
  "/recall/sunset-3.jpg",
  "/recall/dog-3.jpg",
  "/recall/mountain-1.jpg",
]

// three REAL, security-first Premium protections
const FEATURES = [
  {
    key: "lock",
    Icon: svg.featureIcons.PatternLock,
    name: "Only-you lock",
    line: "Face ID, Touch ID, or a code you choose",
  },
  {
    key: "cloud",
    Icon: svg.featureIcons.CloudVault,
    name: "Encrypted Cloud Vault",
    line: "a private, encrypted backup only for you",
  },
  {
    key: "decoy",
    Icon: svg.featureIcons.Decoy,
    name: "Decoy Vault",
    line: "a second code opens a harmless fake",
  },
]

// the code the auto-demo taps in (visual only — never a real secret)
const DEMO_CODE = [7, 3, 0, 9, 1, 4]
const KEYS = [1, 2, 3, 4, 5, 6, 7, 8, 9]

const OnlyYouPaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$2.99"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"

  const savingsPct = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 79
    const p = Math.round(
      (1 - yearlyProduct.price / (monthlyProduct.price * 12)) * 100,
    )
    return p > 0 ? p : 0
  }, [yearlyProduct, monthlyProduct])

  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")

  // ── passcode state ────────────────────────────────────────────────────────
  const [entered, setEntered] = useState<number>(0) // # of dots filled
  const [sealed, setSealed] = useState(false)
  const [flash, setFlash] = useState<number | null>(null) // the evaporating digit
  const [pressedKey, setPressedKey] = useState<number | null>(null)
  const userActed = useRef(false)
  const flashTimer = useRef<number | null>(null)
  const timers = useRef<number[]>([])

  const clearTimers = useCallback(() => {
    timers.current.forEach((t) => clearTimeout(t))
    timers.current = []
    if (flashTimer.current != null) clearTimeout(flashTimer.current)
  }, [])

  const showFlash = useCallback((digit: number) => {
    setFlash(digit)
    if (flashTimer.current != null) clearTimeout(flashTimer.current)
    flashTimer.current = window.setTimeout(() => setFlash(null), 520)
  }, [])

  const pressDigit = useCallback(
    (digit: number) => {
      setPressedKey(digit)
      window.setTimeout(() => setPressedKey(null), 130)
      setEntered((n) => {
        if (n >= CODE_LEN) return n
        const next = n + 1
        showFlash(digit)
        if (next >= CODE_LEN) {
          const t = window.setTimeout(() => setSealed(true), 340)
          timers.current.push(t)
        }
        return next
      })
    },
    [showFlash],
  )

  const onKeyTap = useCallback(
    (digit: number) => {
      userActed.current = true
      clearTimers()
      if (sealed) {
        // tapping after a seal resets so the mechanic stays explorable
        setSealed(false)
        setEntered(0)
        setFlash(null)
        return
      }
      pressDigit(digit)
    },
    [sealed, pressDigit, clearTimers],
  )

  const onDelete = useCallback(() => {
    userActed.current = true
    clearTimers()
    setSealed(false)
    setEntered((n) => Math.max(0, n - 1))
    setFlash(null)
  }, [clearTimers])

  // auto-demo on mount: type the code so the mechanic + payoff read at a glance
  useEffect(() => {
    if (reduceMotion) {
      setEntered(CODE_LEN)
      setSealed(true)
      return
    }
    let delay = 900
    DEMO_CODE.forEach((digit, i) => {
      const t = window.setTimeout(() => {
        if (userActed.current) return
        setPressedKey(digit)
        window.setTimeout(
          () => setPressedKey((k) => (k === digit ? null : k)),
          130,
        )
        setEntered(i + 1)
        showFlash(digit)
      }, delay)
      timers.current.push(t)
      delay += 460
    })
    const seal = window.setTimeout(() => {
      if (userActed.current) return
      setSealed(true)
    }, delay + 120)
    timers.current.push(seal)
    return clearTimers
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduceMotion])

  const handleSubscribe = useCallback(() => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
    else native.postNativeEvent("subscribe")
  }, [plan, yearlyProduct, monthlyProduct])

  return (
    <div
      className={`${styles.root} ${sealed ? styles.isSealed : ""}`}
      style={{ ["--filled" as any]: entered }}
    >
      <div className={styles.warmth} aria-hidden="true" />

      {/* header — brand clarity in the first glance */}
      <header className={styles.top}>
        <div className={styles.brand}>
          <span className={styles.brandMark} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="16" height="16">
              <path
                d="M12 2.6l6.8 2.6v5.3c0 4.4-2.9 8-6.8 9.4-3.9-1.4-6.8-5-6.8-9.4V5.2L12 2.6z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
              />
              <path
                d="M9 12.2l2.2 2.2L15 10.4"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className={styles.brandName}>
            Private Photo Vault
            <span className={styles.brandTag}>Premium</span>
          </span>
        </div>
        <button
          className={styles.close}
          onClick={() => native.postNativeEvent("close")}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" width="15" height="15">
            <path
              d="M6 6l12 12M18 6L6 18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </header>

      {/* intro */}
      <section className={styles.intro}>
        <span className={styles.kicker}>
          <span className={styles.kickerDot} aria-hidden="true" />
          Only-you access
        </span>
        <h1 className={styles.title}>
          The only key is the one
          <br />
          <span className={styles.titleAccent}>only you know.</span>
        </h1>
        <p className={styles.sub}>
          Not stored on a server. Not scanned off your face. Your vault opens
          for a number that lives nowhere but your memory.
        </p>
      </section>

      {/* ── the vault passcode screen ──────────────────────────────────────── */}
      <section className={styles.pad}>
        {/* the sealed library, faint behind the pad */}
        <div className={styles.mosaic} aria-hidden="true">
          {MOSAIC.map((src, i) => (
            <span
              key={src}
              className={styles.chip}
              style={{ ["--i" as any]: i }}
            >
              <img src={src} alt="" draggable={false} />
            </span>
          ))}
          <span className={styles.mosaicVeil} />
        </div>

        {/* the lock + status */}
        <div className={styles.padInner}>
          <span className={styles.lock} aria-hidden="true">
            <span className={styles.lockShackle} />
            <span className={styles.lockBody}>
              <svg viewBox="0 0 24 24" width="18" height="18">
                <path
                  className={styles.lockOpen}
                  d="M5 12.6l4.3 4.3L19 7.4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </span>

          <div className={styles.status} aria-live="polite">
            {sealed ? "Sealed — opens only for you" : "Enter your passcode"}
          </div>

          {/* the code dots */}
          <div
            className={styles.dots}
            role="status"
            aria-label={`${entered} of ${CODE_LEN} digits entered`}
          >
            {Array.from({ length: CODE_LEN }).map((_, i) => (
              <span
                key={i}
                className={`${styles.dot} ${i < entered ? styles.dotOn : ""}`}
              />
            ))}
            {flash != null && !sealed && (
              <span
                key={`f${entered}`}
                className={styles.flash}
                aria-hidden="true"
              >
                {flash}
              </span>
            )}
          </div>

          <p className={styles.noStore}>
            <span className={styles.noStoreDot} aria-hidden="true" />
            Nothing stored — the code lives only in your memory
          </p>

          {/* the numeric keypad */}
          <div
            className={styles.keys}
            role="group"
            aria-label="Passcode keypad"
          >
            {KEYS.map((n) => (
              <button
                key={n}
                className={`${styles.key} ${pressedKey === n ? styles.keyDown : ""}`}
                onClick={() => onKeyTap(n)}
                aria-label={`Digit ${n}`}
              >
                {n}
              </button>
            ))}
            <span className={styles.keyFace} aria-hidden="true">
              <svg viewBox="0 0 24 24" width="20" height="20">
                <path
                  d="M4 8V6a2 2 0 0 1 2-2h2M16 4h2a2 2 0 0 1 2 2v2M20 16v2a2 2 0 0 1-2 2h-2M8 20H6a2 2 0 0 1-2-2v-2"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
                <circle cx="9.3" cy="10.5" r="0.9" fill="currentColor" />
                <circle cx="14.7" cy="10.5" r="0.9" fill="currentColor" />
                <path
                  d="M12 10v3.2M10 15.4c.6.5 1.4.7 2 .7s1.4-.2 2-.7"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </span>
            <button
              className={`${styles.key} ${pressedKey === 0 ? styles.keyDown : ""}`}
              onClick={() => onKeyTap(0)}
              aria-label="Digit 0"
            >
              0
            </button>
            <button
              className={`${styles.key} ${styles.keyAlt}`}
              onClick={onDelete}
              aria-label="Delete"
            >
              <svg viewBox="0 0 24 24" width="21" height="21">
                <path
                  d="M9 5h9a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H9L3 12l6-7z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinejoin="round"
                />
                <path
                  d="M12.5 9.5l4 4M16.5 9.5l-4 4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>

          <p className={styles.orLine}>or unlock with Face ID · Touch ID</p>
        </div>
      </section>

      {/* three real, security-first protections */}
      <ul className={styles.features}>
        {FEATURES.map((f) => {
          const Icon = f.Icon
          return (
            <li key={f.key} className={styles.feature}>
              <span className={styles.featureIcon} aria-hidden="true">
                <Icon />
              </span>
              <span className={styles.featureText}>
                <span className={styles.featureName}>{f.name}</span>
                <span className={styles.featureLine}>{f.line}</span>
              </span>
            </li>
          )
        })}
      </ul>

      {/* honest, compliant reassurance */}
      <p className={styles.assure}>
        <span className={styles.assureMark} aria-hidden="true">
          <svg viewBox="0 0 24 24" width="13" height="13">
            <path
              d="M12 2.6l6.6 2.5v5.2c0 4.3-2.8 7.7-6.6 9.1-3.8-1.4-6.6-4.8-6.6-9.1V5.1L12 2.6z"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        The keypad is the vault&rsquo;s real passcode lock. Your only-you lock,
        the encrypted Cloud Vault and the Decoy Vault are real Private Photo
        Vault features. Your photos are always yours — sealing protects who can
        open them, never whether you keep them.
      </p>

      {/* ── PRICING: yearly featured, monthly small & secondary ───────────── */}
      <div className={styles.plans}>
        <button
          className={`${styles.yearly} ${
            plan === "yearly" ? styles.selected : ""
          }`}
          onClick={() => setPlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.yearlyGlow} aria-hidden="true" />
          <span className={styles.yearlyHead}>
            <span className={styles.yearlyName}>Yearly · make it yours</span>
            {savingsPct > 0 && (
              <span className={styles.saveTag}>Save {savingsPct}%</span>
            )}
          </span>
          <span className={styles.yearlyPriceRow}>
            <span className={styles.yearlyPrice}>{yearlyPrice}</span>
            <span className={styles.yearlyUnit}>/ year</span>
            <span className={styles.check} aria-hidden="true">
              <svg viewBox="0 0 24 24" width="13" height="13">
                <path
                  d="M5 12.5l4.2 4.2L19 7"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </span>
          <span className={styles.yearlyPerMo}>
            7 days free, then about {yearlyPerMonth}/mo billed yearly
          </span>
        </button>

        <button
          className={`${styles.monthly} ${
            plan === "monthly" ? styles.monthlySel : ""
          }`}
          onClick={() => setPlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.monthlyName}>Monthly</span>
          <span className={styles.monthlyPrice}>{monthlyPrice}/mo</span>
        </button>
      </div>

      <div className={styles.footerLinks}>
        <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
        <span>·</span>
        <button onClick={() => native.postNativeEvent("privacy")}>
          Privacy
        </button>
        <span>·</span>
        <button onClick={() => native.postNativeEvent("already_purchased")}>
          Restore
        </button>
      </div>

      <p className={styles.credit}>Demo library from Unsplash</p>

      <div className={styles.dock}>
        <button className={styles.cta} onClick={handleSubscribe}>
          <span className={styles.ctaMain}>
            {plan === "yearly"
              ? "Start free — make it only yours"
              : "Start monthly"}
          </span>
          <span className={styles.ctaSub}>
            {plan === "yearly"
              ? `7 days free, then ${yearlyPrice}/yr · cancel anytime`
              : `${monthlyPrice}/mo · cancel anytime`}
          </span>
        </button>
      </div>
    </div>
  )
}

export default OnlyYouPaywall
